import { toRaw } from "vue";
import type { MastheadUploadConfig } from "@/types/types";
import { format as dateformat } from "date-fns/format";
import type { MasterTemplate } from "shared-types";
import { convertIndesignDimensionToPixels, formatIndesignDimension } from "shared-utils/utils/indesignMeasurementUnit";

export { default as getObjectValue } from "shared-utils/utils/getObjectValue";

const DATE_FORMAT = "dd/MM/yyyy HH:mm";

export const camelToUpperCase = (string: string) =>
    string
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([a-z])([0-9])/, "$1 $2")
        .replace(/^./, name => name.toUpperCase());

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const formatEditableName = (editableName: string) => camelToUpperCase(editableName);

export const formatGroupName = (groupName: string) => groupName.replace(/(?:^|\s)\S/g, name => name.toUpperCase());

export const formatTimestamp = (value?: number) => (value ? dateformat(value * 1000, DATE_FORMAT) : `${value}`);

export const bytesToSize = (bytes: number) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

    if (bytes === 0) {
        return "n/a";
    }

    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString(), 10);

    if (i === 0) {
        return `${bytes} ${sizes[i]}`;
    }

    return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

// Recursively unwrap all Vue proxies before cloning
function deepToRaw<U>(val: U): U {
    const unwrapped = toRaw(val);

    // Handle null and primitives
    if (unwrapped === null || typeof unwrapped !== "object") {
        return unwrapped;
    }

    // Handle arrays
    if (Array.isArray(unwrapped)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return unwrapped.map(item => deepToRaw(item)) as U;
    }

    // Handle plain objects
    if (Object.prototype.toString.call(unwrapped) === "[object Object]") {
        const result: Record<string, unknown> = {};
        for (const key in unwrapped) {
            if (Object.prototype.hasOwnProperty.call(unwrapped, key)) {
                result[key] = deepToRaw((unwrapped as Record<string, unknown>)[key]);
            }
        }
        return result as U;
    }

    return unwrapped;
}

export const deepClone = <T>(data: T): T => {
    return structuredClone(deepToRaw(data));
};

export const dedup = <T extends Record<K, string | number>, K extends keyof T>(data: T[], key: K) =>
    Object.values(data.reduce<Record<string, T>>((acc, cur) => Object.assign(acc, { [cur[key]]: cur }), {}));

export const numberWithCommas = (num: number) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const splitFileNameAndExtension = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) {
        return {
            extension: "",
            name: fileName
        };
    }

    return {
        extension: fileName.slice(lastDotIndex),
        name: fileName.slice(0, lastDotIndex)
    };
};

/*
    This makes a massive assumption that the file name is the last part of the URL.
*/
export const getFileNameFromUrl = (url: string) => {
    try {
        const urlParts = new URL(url).pathname.split("/");
        let fileName = urlParts.pop();
        if (fileName === "0") {
            fileName = urlParts[urlParts.length - 1];
        }
        if (!fileName) {
            return url;
        }
        return fileName;
    } catch {
        return url;
    }
};

export function getImageDimensionsFromUrl(url: string) {
    return new Promise<{ width: number; height: number }>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({
                height: img.naturalHeight,
                width: img.naturalWidth
            });
        };
        img.onerror = err => {
            reject(new Error(typeof err === "string" ? err : "Image failed to load"));
        };
        img.src = url;
    });
}

export const isEmptyObject = (obj: Record<string, unknown>) => Object.getOwnPropertyNames(obj).length === 0;

export function debounce<T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number,
    immediate = false
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    return function debounced(this: ThisParameterType<T>, ...args: Parameters<T>) {
        const later = () => {
            timeout = undefined;
            if (!immediate) {
                func.apply(this, args);
            }
        };

        if (immediate && !timeout) {
            func.apply(this, args);
        }

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export const formatTemplateSizeLabel = (
    template: Pick<MasterTemplate, "width" | "height" | "adType"> & { measurementUnit?: string }
): string => {
    if (template.adType === "INDESIGN") {
        const measurementUnit = template.measurementUnit || "points";
        const w = formatIndesignDimension(template.width, measurementUnit);
        const h = formatIndesignDimension(template.height, measurementUnit);
        return `${w} x ${h}`;
    }
    return `${template.width} x ${template.height}`;
};

export const getTemplateDisplayDimensions = (
    template: Pick<MasterTemplate, "width" | "height" | "adType"> & { measurementUnit?: string }
) => {
    if (template.adType === "INDESIGN") {
        const measurementUnit = template.measurementUnit || "points";
        return {
            width: convertIndesignDimensionToPixels(template.width, measurementUnit),
            height: convertIndesignDimensionToPixels(template.height, measurementUnit)
        };
    }

    return {
        width: template.width,
        height: template.height
    };
};

export const generateTemplateLabel = ({ height, width }: { height: number; width: number }, usedValues: string[]) => {
    let value = `${width}x${height}`;
    let i = 0;

    while (usedValues.includes(value)) {
        if (value.includes(`_${i}`)) {
            value = value.replace(`_${i}`, `_${(i += 1)}`);
        } else {
            value = `${value}_${(i += 1)}`;
        }
    }

    return value;
};

export const sanitizeHTML = (str: string) => {
    const temp = document.createElement("div");
    temp.textContent = str;
    return temp.innerHTML;
};

export const generateMasterTemplateFilterOptions = (masterTemplates: MasterTemplate[]) => {
    /*
        We do this sort so that the same master templates get suffixed with
        the same number (if there are no changes to the master templates)
        by the generateLabel function if there are multiple master templates
        of the same size.
    */
    const masterTemplatesSortedById = [...masterTemplates].sort((a, b) => a.created - b.created);

    const generatedTemplateLabels: string[] = [];
    const masterTemplateOptions: { height: number; label: string; value: string; width: number }[] = [];
    masterTemplatesSortedById.forEach(masterTemplate => {
        const label = generateTemplateLabel(masterTemplate, generatedTemplateLabels);
        generatedTemplateLabels.push(label);
        masterTemplateOptions.push({
            height: masterTemplate.height,
            label,
            value: masterTemplate._id,
            width: masterTemplate.width
        });
    });
    /*
        We sort the options by label because it's nicer if the options
        are sorted by dimensions in the UI.
    */
    const masterTemplateOptionsSortedByDimensions = [...masterTemplateOptions].sort((a, b) => {
        if (a.width - b.width !== 0) {
            return a.width - b.width;
        }
        if (a.height - b.height !== 0) {
            return a.height - b.height;
        }
        if (a.label < b.label) {
            return -1;
        }
        if (a.label > b.label) {
            return 1;
        }
        return 0;
    });
    return masterTemplateOptionsSortedByDimensions;
};

// I'm sorry ;(
export const isIe = () => navigator.userAgent.includes("Trident");

export const uploadConfigExpired = (uploadConfig: MastheadUploadConfig | undefined) => {
    if (!uploadConfig) {
        return false;
    }

    try {
        // Check if the one we have is expired or not
        const { postData: postDataRaw } = uploadConfig;
        const postData = JSON.parse(postDataRaw) as {
            bucket: string;
            "X-Amz-Algorithm": string;
            "X-Amz-Credential": string;
            "X-Amz-Date": string;
            "X-Amz-Security-Token": string;
            key: string;
            Policy: string;
            "X-Amz-Signature": string;
        };

        if (!postData.Policy) {
            return true;
        }
        const policy = JSON.parse(atob(postData.Policy)) as {
            expiration: string;
            bucket: string;
        };

        if (!policy.expiration) {
            return true;
        }
        // Subtracting the current time from the expiry time gives us the number of ms until the
        //  token expires. If that time is longer than 300s (5 mins) then we request a new token
        const expireDate = new Date(policy.expiration);
        const timeToExpireInSeconds = (expireDate.getTime() - Date.now()) / 1000;
        if (timeToExpireInSeconds < 300) {
            return true;
        }
    } catch (err) {
        console.error(err);
    }

    return false;
};

export const kebabCase = (string: string) =>
    string
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/\s+/g, "-")
        .toLowerCase();

export const objectIsEmpty = (obj: Record<string, unknown> | undefined) =>
    obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;

export const isValidHttpUrl = (string: string) => {
    let url;

    try {
        url = new URL(string);
    } catch {
        return false;
    }

    return url.protocol === "http:" || url.protocol === "https:";
};

const LP_PROVIDER = "Okta-OIDC-IC-LandingPage";

const lpProviders = {
    [LP_PROVIDER]: [
        "hogarthww.com",
        "hogarth.com",
        "mindshareworld.com",
        "wpp.com",
        "wppcoretech.com",
        "jwt.com",
        "phdmedia.com",
        "annalect.com",
        "gtb.com",
        "wppproduction.com",
        "wppmedia.com"
    ]
};

const lpProviderKeys = Object.keys(lpProviders);

export const getProvider = (email: string) => {
    const domain = email.split("@").pop();

    if (!domain) return undefined;

    return lpProviderKeys.find(p => lpProviders[p as keyof typeof lpProviders].includes(domain));
};

export const checkEmptySpaces = (value: string) => /^[^\s]/.test(value);
export const checkOnlyNumbers = (value: string) => /^\d+$/.test(value);

export { default as constructLibraryFiltersQueryVariables } from "@/utils/constructLibraryFiltersQueryVariables";
export { default as snackbar } from "@/utils/snackbar";
